'use client'

import { useEffect, useMemo, useState } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DateInput } from '@/components/ui/date-input'
import { toast } from 'sonner'
import { Trash2, CalendarHeart, Plus, PartyPopper, Moon, Pencil, X } from 'lucide-react'
import { GlassCard } from '@/components/ui/glass-card'
import { SectionHeader } from '@/components/ui/section-header'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { PRIMARY_BTN } from '@/components/ui/button-styles'
import { MonthGrid } from '@/components/ui/month-grid'
import { todayIST } from '@/lib/date-utils'
import { cn } from '@/lib/utils'

interface Holiday {
  id: string
  date: string
  name: string
  declaredAt: string
}

export function HolidayManager() {
  const [holidays, setHolidays] = useState<Holiday[]>([])
  const [date, setDate] = useState('')
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  // When set, the form above edits this holiday instead of adding a new one.
  const [editingId, setEditingId] = useState<string | null>(null)
  // Separate from `loading` (which gates the Add button) so the list can show a
  // loading body instead of flashing an empty state while the first fetch runs.
  const [fetching, setFetching] = useState(true)
  const [month, setMonth] = useState(() => todayIST().slice(0, 7))

  const holidayByDate = useMemo(() => {
    const map: Record<string, Holiday> = {}
    for (const h of holidays) if (h?.date) map[h.date] = h
    return map
  }, [holidays])

  const holidaysThisMonth = useMemo(
    () => holidays.filter(h => h.date?.startsWith(month)).length,
    [holidays, month]
  )

  const load = async () => {
    try {
      const res = await apiFetch('/api/holidays')
      const data = await res.json()
      setHolidays(data.holidays || [])
    } catch (e: any) {
      toast.error(e.message || 'Failed to load holidays')
    } finally {
      setFetching(false)
    }
  }
  useEffect(() => { load() }, [])

  const save = async () => {
    if (!date || !name) return toast.error('Date and name required')
    setLoading(true)
    try {
      if (editingId) {
        await apiFetch(`/api/holidays/${editingId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ date, name }),
        })
        toast.success('Holiday updated')
        setEditingId(null)
      } else {
        await apiFetch('/api/holidays', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ date, name }),
        })
        toast.success('Holiday added')
      }
      setDate(''); setName('')
      load()
    } catch (e: any) {
      toast.error(e.message)
    } finally { setLoading(false) }
  }

  const startEdit = (h: Holiday) => {
    setEditingId(h.id)
    setDate(h.date)
    setName(h.name)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setDate('')
    setName('')
  }

  const del = async (id: string) => {
    try {
      await apiFetch(`/api/holidays/${id}`, { method: 'DELETE' })
      toast.success('Removed')
      load()
    } catch (e: any) {
      toast.error(e.message || 'Delete failed')
    }
  }

  const fmtDate = (iso: string) => {
    try {
      const d = new Date(iso + 'T00:00:00')
      return d.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
    } catch { return iso }
  }

  return (
    // No PageShell: only renders nested inside PerformanceAnalytics' shell.
    <div className="space-y-5 md:space-y-6">
      <SectionHeader icon={CalendarHeart} title="Holidays" description="Declare company-wide holidays" />

      <GlassCard inset tone={editingId ? 'orange' : 'violet'}>
        <div className="p-4 md:p-5 flex flex-col sm:flex-row gap-2">
          <DateInput
            value={date}
            onChange={(value) => setDate(value)}
            placeholder="Select holiday date"
            className="sm:max-w-[200px]"
          />
          <Input
            placeholder={editingId ? 'Edit holiday name' : 'Holiday name'}
            value={name}
            onChange={e => setName(e.target.value)}
          />
          <Button onClick={save} disabled={loading} className={cn('w-full sm:w-auto', PRIMARY_BTN)}>
            {editingId
              ? <><Pencil className="h-4 w-4 mr-1" /> Save changes</>
              : <><Plus className="h-4 w-4 mr-1" /> Add</>}
          </Button>
          {editingId && (
            <Button variant="outline" onClick={cancelEdit} className="w-full sm:w-auto border-neutral-200 dark:border-white/10">
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
          )}
        </div>
      </GlassCard>

      {/* Month view: which days are already declared, at a glance. Clicking a
          day loads it into the form above instead of forcing a date picker. */}
      <GlassCard>
        <MonthGrid
          month={month}
          onMonthChange={setMonth}
          onDayClick={(d) => { setDate(d); toast.info(`${fmtDate(d)} selected — add a name above`) }}
          hint="Click a day to load it into the form above."
          toolbar={
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-500 dark:text-white/50">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-cyan-500" />
                Holiday
                <span className="tabular-nums font-medium text-neutral-700 dark:text-white/70">{holidaysThisMonth}</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-neutral-300 dark:bg-white/20" />
                Weekend
              </span>
            </div>
          }
          renderDay={(d, meta) => {
            const holiday = holidayByDate[d]
            if (holiday) {
              return {
                title: `${d} · ${holiday.name}`,
                className: 'bg-cyan-50 ring-cyan-200 hover:ring-cyan-300 dark:bg-cyan-500/10 dark:ring-cyan-500/25 dark:hover:ring-cyan-500/40',
                content: (
                  <>
                    <PartyPopper className="h-4 w-4 text-cyan-600 dark:text-cyan-300 sm:h-[18px] sm:w-[18px]" />
                    <span className="max-w-full truncate text-[9px] leading-none text-cyan-700 dark:text-cyan-300 sm:text-[10px]">
                      {holiday.name}
                    </span>
                  </>
                ),
              }
            }
            if (meta.isWeekend) {
              return {
                title: `${d} · Weekend`,
                className: 'bg-neutral-50 ring-neutral-200/70 dark:bg-white/[0.02] dark:ring-white/5',
                content: <Moon className="h-4 w-4 text-neutral-300 dark:text-white/20 sm:h-[18px] sm:w-[18px]" />,
              }
            }
            return { title: d, className: 'bg-transparent ring-neutral-200/60 dark:ring-white/5' }
          }}
        />
      </GlassCard>

      {fetching ? (
        <GlassCard>
          <TabLoader />
        </GlassCard>
      ) : holidays.length === 0 ? (
        <GlassCard>
          <EmptyState
            icon={CalendarHeart}
            title="No holidays declared"
            description="Add a date above to mark it as a company-wide holiday."
          />
        </GlassCard>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {holidays.map(h => (
            <GlassCard key={h.id} tone="violet" interactive>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-neutral-900 dark:text-white/90 font-medium truncate">{h.name}</p>
                  <p className="text-xs text-neutral-500 dark:text-white/50 mt-1">{fmtDate(h.date)}</p>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button
                    size="icon"
                    variant="ghost"
                    title="Edit holiday"
                    onClick={() => startEdit(h)}
                    className="text-cyan-600 dark:text-cyan-300 hover:bg-cyan-500/10"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => del(h.id)} className="text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 hover:bg-rose-500/10">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  )
}
